import { getBorder } from 'constants/Style';
import { ComponentProps, useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { Check, KeyboardArrowDown } from '@mui/icons-material';
import { Effects, SystemColors } from 'melp-design/style';
import { Chip } from 'melp-design/components';

interface Option<Value> {
  value: Value;
  label: string;
  variant?: ComponentProps<typeof Chip>['variant'];
}

interface Props<Value> {
  value: Value;
  onChange: (value: Value) => void;
  options: Option<Value>[];
  disabled?: boolean;
}

export const ChipSelectInput = <Value extends string | number>({
  value,
  onChange,
  options,
  disabled,
}: Props<Value>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const selectedOption = options.find((option) => option.value === value);

  const onClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Chip
        label={selectedOption?.label ?? ''}
        variant={selectedOption?.variant}
        icon={KeyboardArrowDown}
        onClick={
          disabled ? undefined : (event) => setAnchorEl(event.currentTarget)
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={onClose}
        sx={{
          '& .MuiPaper-root': {
            border: getBorder(SystemColors.grey[90]),
            boxShadow: Effects.smallElementsShadow,
            maxHeight: 400,
          },
          '& .MuiMenu-list': {
            padding: '5px',
          },
        }}
      >
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <MenuItem
              key={option.value}
              selected={isSelected}
              onClick={() => {
                onChange(option.value);
                onClose();
              }}
              sx={{
                // Overrides Melp theme MenuItem `padding: 8px 10px !important`
                p: '5px !important',
                minHeight: 'auto',
                gap: 0.5,
                backgroundColor: 'transparent',
                '&:hover, &.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: 'transparent',
                },
                '& *': {
                  cursor: 'pointer',
                },
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {isSelected ? (
                  <Check sx={{ fontSize: 16, color: SystemColors.black }} />
                ) : null}
              </Box>
              <Chip label={option.label} variant={option.variant} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
